import React, { useEffect } from 'react';
import * as mobxReact from 'mobx-react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { ManageEventNavigation } from '../shared/navigation/navigation';
import DefaultButton from '../../../global/default-button/default-button';
import { hasPermission } from '../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../api/roles.dao';
import { ITheadSort, ListLayout } from '../../../global/tables/table-list-layout';
import TableLoadingRow from '../../../global/tables/table-loading-row';
import { IReportDetailsDTO, ReportType, TGetReportsSortKeys } from '../../../../api/reports.dao';
import ReportsViewModel from './reports.viewmodel';
import { ReportListItem } from './_report-list-item/report-list-item';
import './reports.scss';
import OptionsButton from '../../../global/options-button/options-button';
import { GenerateZoomReportPopover } from './generate-zoom-registration-report-popover/generate-zoom-report-popover';
import { ReportTypeDescriptions } from '../../../../constants/report-type-descriptions.constants';
import { SelectReportPopover } from './_select-report-popover/select-report-popover';
import { MORE_BUTTON_TEXT } from '../../../../constants/common-labels.constants';

interface IProps {
    vm: ReportsViewModel;
    intl: IntlShape;
}

const ReportsPresentation = injectIntl(mobxReact.observer((props: IProps) => {
    const {
        vm,
        intl,
    } = props;

    const {
        reportsList,
        isLoading,
        addReport,
        event,
        fetchReports,
        isGeneratingEventInteractionsReport,
        openGenerateInteractionsReportModal,
        isGenerateZoomReportModalOpen,
        setGenerateZoomReportOpen,
        isGeneratingRegistrantsBySessionReport,
        openGenerateRegistrantsBySessionReportModal,
        generateZoomReportVM,
        setSelectReportOpen,
        isSelectReportModalOpen,
        openAttendeesInteractionReportModal,
        isGeneratingAttendeesInteractionsReport,
        isGeneratingEventRegistrationReport,
        openGenerateEventRegistrationReportModal
    } = vm;

    const theads: ITheadSort<TGetReportsSortKeys>[] = [
        {
            title: intl.formatMessage({
                id: 'oe.reports.dateAndTime',
                defaultMessage: 'Date & Time',
                description: 'Label text'
            }),
        },
        {
            title: intl.formatMessage({
                id: `oe.reports.reportName`,
                description: 'Label text',
                defaultMessage: 'Report Name',
            }),
        },
        {
            title: intl.formatMessage({
                id: `oe.reports.reportType`,
                description: 'Label text',
                defaultMessage: 'Report Type',
            }),
        },
        {
            title: intl.formatMessage({
                id: `oe.reports.createdBy`,
                description: 'Label text',
                defaultMessage: 'Created By',
            }),
        },
        {
            title: intl.formatMessage({
                id: `oe.reports.status`,
                description: 'Label text',
                defaultMessage: 'Status',
            }),
        },
        {
            width: '100px'
        }
    ];

    const isEmpty = !reportsList?.length && !vm.isLoading();
    const canEditReport = hasPermission(Permission.EDIT_REPORT);

    const registrationReports = [
        hasPermission(Permission.EVENT_REGISTRATION_REPORT) && {
            label: intl.formatMessage({
                id: 'oe.reports.eventRegistrationReport',
                defaultMessage: 'Event Registration Report',
                description: 'Button label'
            }),
            action: openGenerateEventRegistrationReportModal,
            disabled: isGeneratingEventRegistrationReport,
            description: intl.formatMessage({
                id: 'oe.reports.eventRegistrationReportDescription',
                defaultMessage: ReportTypeDescriptions.EVENT_REGISTRATION_REPORT,
                description: 'Report type description'
            }),
        },
        hasPermission(Permission.GENERATE_REGISTRANTS_BY_SESSION) && {
            label: intl.formatMessage({
                id: 'oe.reports.registrantsBySession',
                defaultMessage: 'Registrants by Session',
                description: 'Button label'
            }),
            action: openGenerateRegistrantsBySessionReportModal,
            disabled: isGeneratingRegistrantsBySessionReport,
            description: intl.formatMessage({
                id: 'oe.reports.registrantsBySessionDescription',
                defaultMessage: ReportTypeDescriptions.REGISTRANTS_BY_SESSION_REPORT,
                description: 'Report type description'
            }),
        },
        hasPermission(Permission.ZOOM_WEBINAR_REPORT) && {
            label: intl.formatMessage({
                id: 'oe.reports.zoomWebinarRegistrationReport',
                defaultMessage: 'Zoom Webinar Registration Report',
                description: 'Button label'
            }),
            action: () => setGenerateZoomReportOpen(true, ReportType.ZOOM_WEBINAR_REGISTRATION),
            description: intl.formatMessage({
                id: 'oe.reports.zoomWebinarRegistrationReportDescription',
                defaultMessage: ReportTypeDescriptions.ZOOM_WEBINAR_REGISTRATION_REPORT,
                description: 'Report type description'
            }),
        },
        hasPermission(Permission.ZOOM_MEETING_REPORTS) && {
            label: intl.formatMessage({
                id: 'oe.reports.zoomMeetingReports',
                defaultMessage: 'Zoom Meeting Registration Report',
                description: 'Button label'
            }),
            action: () => setGenerateZoomReportOpen(true, ReportType.ZOOM_MEETING_REGISTRATION_REPORT),
            description: intl.formatMessage({
                id: 'oe.reports.zoomMeetingRegistrationReportDescription',
                defaultMessage: ReportTypeDescriptions.ZOOM_MEETING_REGISTRATION_REPORT,
                description: 'Report type description'
            }),
        }
    ].filter(Boolean);

    const postEventReports = [
        hasPermission(Permission.EVENT_INTERACTIONS_REPORT) && {
            label: intl.formatMessage({
                id: 'oe.reports.eventInteractionsReport',
                defaultMessage: 'Event Interactions Report',
                description: 'Button label'
            }),
            action: openGenerateInteractionsReportModal,
            disabled: isGeneratingEventInteractionsReport,
            description: intl.formatMessage({
                id: 'oe.reports.eventInteractionsReportDescription',
                defaultMessage: ReportTypeDescriptions.EVENT_INTERACTIONS_REPORT,
                description: 'Report type description'
            }),
        },
        hasPermission(Permission.ATTENDEE_INTERACTIONS_REPORT) && {
            label: intl.formatMessage({
                id: 'oe.reports.attendeesInteractionReport',
                defaultMessage: 'Attendees Interaction Report',
                description: 'Button label'
            }),
            action: openAttendeesInteractionReportModal,
            disabled: isGeneratingAttendeesInteractionsReport,
            description: intl.formatMessage({
                id: 'oe.reports.attendeesInteractionReportDescription',
                defaultMessage: ReportTypeDescriptions.ATTENDEE_INTERACTION_REPORT,
                description: 'Report type description'
            }),
        },
        hasPermission(Permission.ZOOM_WEBINAR_REPORT) && {
            label: intl.formatMessage({
                id: 'oe.reports.zoomWebinarAttendeeReport',
                defaultMessage: 'Zoom Webinar Attendee Report',
                description: 'Button label'
            }),
            action: () => setGenerateZoomReportOpen(true, ReportType.ZOOM_WEBINAR_ATTENDEE),
            description: intl.formatMessage({
                id: 'oe.reports.zoomWebinarAttendeeReportDescription',
                defaultMessage: ReportTypeDescriptions.ZOOM_WEBINAR_ATTENDEE_REPORT,
                description: 'Report type description'
            }),
        }
    ].filter(Boolean);

    const moreOptions = [
        (registrationReports.length || postEventReports.length) && hasPermission(Permission.UPLOAD_REPORT) && {
            label: intl.formatMessage({
                id: 'oe.reports.uploadFile',
                defaultMessage: 'Upload File',
                description: 'Button label'
            }),
            action: addReport
        },
    ].filter(Boolean);

    useEffect(() => {
        fetchReports();
    }, []);

    return (
        <div>
            <section className='horizontal-row horizontal-row--with-bottom-margin event-tabs'>
                <ManageEventNavigation />
                <div className='horizontal-row__align-to-row-end'>
                    <div className='buttons-block'>
                        {(!!registrationReports.length || !!postEventReports.length) &&
                            <DefaultButton
                                modifier={['fat', 'confirm-safe']}
                                text={
                                    <FormattedMessage
                                        id='oe.reports.generateReport'
                                        defaultMessage='Generate Report'
                                        description='Button label'
                                    />
                                }
                                onClick={() => setSelectReportOpen(true)}
                            />
                        }
                        {!moreOptions.length && hasPermission(Permission.UPLOAD_REPORT) && (
                            <DefaultButton
                                modifier={['fat', 'confirm-safe']}
                                text={<FormattedMessage
                                    id='oe.reports.uploadFile'
                                    defaultMessage='Upload File'
                                    description='Button label'
                                />}
                                onClick={addReport}
                            />
                        )}
                        {!!moreOptions.length && (
                            <OptionsButton
                                modifier={['fat', 'options']}
                                label={intl.formatMessage(MORE_BUTTON_TEXT)}
                                options={moreOptions}
                            />
                        )}
                    </div>
                </div>
            </section>
            <SelectReportPopover
                titlePopover='Select Report Type'
                popoverIsOpen={isSelectReportModalOpen}
                closePopover={() => setSelectReportOpen(false)}
                registrationReports={registrationReports}
                postEventReports={postEventReports}
            />
            <GenerateZoomReportPopover
                popoverIsOpen={isGenerateZoomReportModalOpen}
                closePopover={() => setGenerateZoomReportOpen(false)}
                vm={generateZoomReportVM}
            />
            <section className='fixed-list-layout'>
                <div>
                    <ListLayout
                        sortOptions={{
                            theads: theads,
                            sortRows: vm.setSortParams,
                            sortOrder: vm.sortOrder,
                            sortField: vm.sortField
                        }}
                        isDisabled={isEmpty}
                    >
                        {isLoading() && <TableLoadingRow colSpan={theads.length} />}
                        {!isLoading() && vm.getSortedEmailList().map((item: IReportDetailsDTO) => {
                            return <ReportListItem
                                key={item.assetMetadataId}
                                handleOpen={() => vm.downloadFile(item.assetMetadataId, item.assetName)}
                                eventTimezone={event.timeZone}
                                canEditReport={canEditReport}
                                onEditReport={() => vm.editReport(item)}
                                isSelected={vm.isReportRunFormShown(item.assetMetadataId)}
                                {...item}
                            />;
                        })}
                    </ListLayout>
                    {isEmpty &&
                        <div className='empty-list-message'>
                            <FormattedMessage
                                description='Reports list empty text'
                                defaultMessage='No reports have been uploaded.'
                                id='oe.reports.emptyListText'
                            />
                        </div>
                    }
                </div>
            </section>
        </div>
    );
}));

const ReportsContainer = (props: { vm: ReportsViewModel }) => {
    const { vm } = props;
    return <ReportsPresentation vm={vm} />;
};


export { ReportsContainer as ReportsList, ReportsPresentation };
